import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ChevronLeft } from "lucide-react";

export default function Signup() {
  const nav = useNavigate();
  const { setSession } = useAuth();
  const [form, setForm] = useState({
    company_name: "",
    slug: "",
    admin_name: "",
    admin_email: "",
    admin_password: "",
    phone: "",
  });
  const [agreed, setAgreed] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => {
    let v = e.target.value;
    if (k === "slug") v = v.toLowerCase().replace(/[^a-z0-9-]/g, "-").slice(0, 40);
    setForm({ ...form, [k]: v });
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/signup-tenant", form);
      setSession(data);
      nav(`/t/${data.tenant.slug}/billing/setup`, { replace: true });
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-soft-mint relative overflow-hidden flex items-center justify-center px-4 py-12">
      <div className="blob bg-emerald-200" style={{ width: 320, height: 320, top: -40, right: -60 }} />
      <div className="w-full max-w-lg relative z-10">
        <Link to="/" className="inline-flex items-center gap-1 text-stone-500 hover:text-stone-800 mb-5 text-sm" data-testid="back-home-link">
          <ChevronLeft size={16} /> ホームへ戻る
        </Link>

        <div className="card-soft p-8 md:p-10">
          <h1 className="font-display text-3xl font-black text-stone-900 mb-1">企業を登録</h1>
          <p className="text-stone-600 text-sm mb-6">3分で完了。2週間の無料お試しから。カード登録は不要です。</p>

          <form onSubmit={submit} className="space-y-4">
            <Field label="会社名" required>
              <input data-testid="signup-company-name" required type="text" value={form.company_name} onChange={update("company_name")}
                placeholder="株式会社サンプル" className="input" />
            </Field>
            <Field label="顧客ID" required hint="お好きな英数字で決めてください(例: acme)">
              <input data-testid="signup-slug" required type="text" value={form.slug} onChange={update("slug")}
                placeholder="acme" className="input" pattern="[a-z0-9\-]{2,40}"
                inputMode="email" autoCapitalize="none" autoCorrect="off" spellCheck={false} lang="en" />
            </Field>

            <div className="pt-2 border-t border-stone-100" />
            <Field label="管理者氏名" required>
              <input data-testid="signup-admin-name" required type="text" value={form.admin_name} onChange={update("admin_name")}
                placeholder="山田 太郎" className="input" />
            </Field>
            <Field label="管理者メール" required>
              <input data-testid="signup-admin-email" required type="email" value={form.admin_email} onChange={update("admin_email")}
                placeholder="admin@acme.co.jp" className="input" />
            </Field>
            <Field label="電話番号" required hint="サポートのご連絡や本人確認時に使用します">
              <input data-testid="signup-phone" required type="tel" value={form.phone} onChange={update("phone")}
                placeholder="03-1234-5678" className="input" inputMode="tel" autoComplete="tel" />
            </Field>
            <Field label="パスワード (6文字以上)" required>
              <input data-testid="signup-admin-password" required type="password" minLength={6} value={form.admin_password} onChange={update("admin_password")}
                placeholder="••••••••" className="input" />
            </Field>

            {err && <div data-testid="signup-error" className="bg-rose-50 border border-rose-200 text-rose-700 px-4 py-2.5 rounded-xl text-sm">{err}</div>}

            <label className="flex items-start gap-2.5 p-3 rounded-2xl bg-emerald-50/50 border border-emerald-200 cursor-pointer" data-testid="agree-block">
              <input data-testid="signup-agree-checkbox" type="checkbox" required
                checked={agreed} onChange={(e) => setAgreed(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-emerald-500 cursor-pointer" />
              <span className="text-sm text-stone-700 leading-snug">
                <Link to="/privacy" target="_blank" rel="noreferrer" className="text-emerald-700 font-semibold hover:underline" data-testid="link-privacy">プライバシーポリシー</Link>
                {" および "}
                <Link to="/terms" target="_blank" rel="noreferrer" className="text-emerald-700 font-semibold hover:underline" data-testid="link-terms">利用規約・免責事項</Link>
                {" に同意します"}
              </span>
            </label>

            <button data-testid="signup-submit-btn" type="submit" disabled={loading || !agreed}
              className="w-full rounded-full bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold py-3.5 transition-colors">
              {loading ? "登録中..." : "企業を登録して始める"}
            </button>
          </form>
        </div>
      </div>

      <style>{`.input{ width:100%; padding:.7rem .9rem; border-radius:.75rem; border:1px solid #E7E5E4; background:#FAFAF9; outline:none;} .input:focus{ background:#fff; box-shadow:0 0 0 3px rgba(74,222,128,.25);} `}</style>
    </div>
  );
}

function Field({ label, hint, required, children }) {
  return (
    <label className="block">
      <span className="text-sm font-semibold text-stone-700">{label}{required && <span className="text-rose-500 ml-0.5">*</span>}</span>
      {hint && <span className="block text-xs text-stone-500 mb-1">{hint}</span>}
      <div className="mt-1">{children}</div>
    </label>
  );
}
